import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { User, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

interface Coach {
  id: string;
  full_name: string;
  avatar_url: string | null;
  email: string;
}

interface CoachSelectorProps {
  selectedCoachId: string | null;
  onCoachSelect: (coachId: string) => void;
}

export const CoachSelector = ({ selectedCoachId, onCoachSelect }: CoachSelectorProps) => {
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    try {
      // Get all users with coach role
      const { data: coachRoles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "coach");

      if (rolesError) throw rolesError;

      if (!coachRoles || coachRoles.length === 0) {
        setCoaches([]);
        setLoading(false);
        return;
      }

      const coachIds = coachRoles.map(r => r.user_id);

      // Get profiles for all coaches
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, email")
        .in("id", coachIds);

      if (profilesError) throw profilesError;

      setCoaches(profiles || []);

      // Auto-select if only one coach
      if (profiles && profiles.length === 1) {
        onCoachSelect(profiles[0].id);
      }
    } catch (error) {
      console.error("Error fetching coaches:", error);
      toast.error("Failed to load coaches");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-border/40 shadow-xl">
        <CardHeader>
          <CardTitle className="text-xl">Loading coaches...</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (coaches.length === 0) {
    return null;
  }

  // If only one coach, auto-select them but don't show selector
  if (coaches.length === 1) {
    return null;
  }

  // Multiple coaches - show selection grid
  return (
    <Card className="border-2 border-primary/30 shadow-xl bg-gradient-to-br from-card via-card to-primary/5">
      <CardHeader>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl">Select Your Coach</CardTitle>
            <CardDescription>Choose who you'd like to learn from</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          {coaches.map((coach) => (
            <button
              key={coach.id}
              onClick={() => onCoachSelect(coach.id)}
              className={`relative p-4 rounded-lg border-2 transition-all hover:scale-105 text-left ${
                selectedCoachId === coach.id
                  ? "border-primary bg-primary/10 shadow-lg"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={coach.avatar_url || undefined} alt={coach.full_name} />
                  <AvatarFallback>
                    <User className="w-6 h-6" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h4 className="font-semibold">{coach.full_name}</h4>
                  <p className="text-sm text-muted-foreground">{coach.email}</p>
                </div>
                {selectedCoachId === coach.id && (
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                )}
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
